import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogContent,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { showToast } from '@utils';
import { apiTradeIndividualOrder, apiInsertOrder } from '@store';

// ── Fake data for test bid/ask ──────────────────────────────────────────────
// const FAKE_CONTRACTS = ['DA','CAL-26', 'CAL-27', 'CAL-28', 'Q1-26', 'Q2-26'];
const FAKE_CONTRACTS = ['DA'];
// const FAKE_PRODUCTS = ['TTF', 'THE', 'PEG'];
const FAKE_PRODUCTS = ['TTF'];

const generateFakeEntry = (side) => {
    const contract =
        FAKE_CONTRACTS[Math.floor(Math.random() * FAKE_CONTRACTS.length)];
    const product =
        FAKE_PRODUCTS[Math.floor(Math.random() * FAKE_PRODUCTS.length)];
    const price = (Math.random() * 100 + 20).toFixed(3);
    const quantity = Math.floor(Math.random() * 500 + 10);

    return {
        productName: product,
        contract,
        price,
        quantity,
        side
    };
};

// ── Shared helpers ──────────────────────────────────────────────────────────
const STATUS_OPTIONS = ['Firm'];
const EXPIRY_OPTIONS = ['Good Till Cancelled'];

const buildInsertPayload = (entry, values, action) => ({
    engine_id: '1',
    price: Number(values.price),
    volume: Number(values.quantity),
    side: action === 'Bid' ? 'BID' : 'ASK',
    inst_name: entry.productName || 'THE EEX',
    first_sequence_id: 0,
    first_sequence_item_name: entry.contract || '',
    all_or_none: Boolean(values.allOrNone),
    broker_id: 1,
    route_id: 1
});

const buildDealPayload = (entry, values) => ({
    engine_id: '1',
    order_id: `test-order-${Date.now()}`,
    broker_id: 1,
    volume: Number(values.quantity),
    inst_name: entry.productName || 'THE EEX',
    first_sequence_id: 0,
    first_sequence_item_name: entry.contract || ''
});

// ── Add Bid Form ────────────────────────────────────────────────────────────
function AddBidForm({ entry, onSubmit, loading }) {
    const [values, setValues] = useState({
        price: entry.price || '',
        quantity: entry.quantity || '',
        allOrNone: false,
        status: 'Firm',
        expiry: 'Good Till Cancelled'
    });

    const handleChange = (field) => (e) => {
        const next = field === 'allOrNone' ? e.target.checked : e.target.value;
        setValues((cur) => ({ ...cur, [field]: next }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(buildInsertPayload(entry, values, 'Bid'));
    };

    return (
        <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
                <Box
                    sx={{
                        px: 2,
                        py: 2,
                        borderRadius: 2,
                        bgcolor: '#eaf3fb',
                        textAlign: 'center'
                    }}
                >
                    <Typography
                        sx={{ fontSize: 28, fontWeight: 700, color: '#0f5b94' }}
                    >
                        {entry.productName}
                    </Typography>
                    <Typography
                        sx={{ fontSize: 20, fontWeight: 600, color: '#0f5b94' }}
                    >
                        {entry.contract}
                    </Typography>
                    <Typography
                        sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}
                    >
                        🧪 Test Mode — Fake Data
                    </Typography>
                </Box>

                <Stack direction="row" spacing={2}>
                    <TextField
                        label="Price"
                        type="number"
                        fullWidth
                        size="small"
                        value={values.price}
                        onChange={handleChange('price')}
                        inputProps={{ step: 'any' }}
                    />
                    <TextField
                        label="Quantity"
                        type="number"
                        fullWidth
                        size="small"
                        value={values.quantity}
                        onChange={handleChange('quantity')}
                        inputProps={{ step: 'any', min: 0 }}
                        disabled={values.allOrNone}
                        helperText={
                            values.allOrNone ? 'Quantity locked (AoN)' : ''
                        }
                    />
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={values.allOrNone}
                                onChange={handleChange('allOrNone')}
                            />
                        }
                        label="AoN"
                    />
                </Stack>

                <Stack direction="row" spacing={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={values.status}
                            label="Status"
                            onChange={handleChange('status')}
                        >
                            {STATUS_OPTIONS.map((s) => (
                                <MenuItem key={s} value={s}>
                                    {s}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                        <InputLabel>Expiry</InputLabel>
                        <Select
                            value={values.expiry}
                            label="Expiry"
                            onChange={handleChange('expiry')}
                        >
                            {EXPIRY_OPTIONS.map((e) => (
                                <MenuItem key={e} value={e}>
                                    {e}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="space-between"
                >
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={
                            loading ||
                            values.price === '' ||
                            values.quantity === ''
                        }
                        sx={{
                            minWidth: 140,
                            bgcolor: '#29a000',
                            '&:hover': { bgcolor: '#228400' }
                        }}
                    >
                        {loading ? (
                            <CircularProgress
                                size={20}
                                sx={{ color: 'white' }}
                            />
                        ) : (
                            'Bid'
                        )}
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
}

// ── Add Ask Form ────────────────────────────────────────────────────────────
function AddAskForm({ entry, onSubmit, loading }) {
    const [values, setValues] = useState({
        price: entry.price || '',
        quantity: entry.quantity || '',
        allOrNone: false,
        status: 'Firm',
        expiry: 'Good Till Cancelled'
    });

    const handleChange = (field) => (e) => {
        const next = field === 'allOrNone' ? e.target.checked : e.target.value;
        setValues((cur) => ({ ...cur, [field]: next }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(buildInsertPayload(entry, values, 'Ask'));
    };

    return (
        <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
                <Box
                    sx={{
                        px: 2,
                        py: 2,
                        borderRadius: 2,
                        bgcolor: '#eaf3fb',
                        textAlign: 'center'
                    }}
                >
                    <Typography
                        sx={{ fontSize: 28, fontWeight: 700, color: '#0f5b94' }}
                    >
                        {entry.productName}
                    </Typography>
                    <Typography
                        sx={{ fontSize: 20, fontWeight: 600, color: '#0f5b94' }}
                    >
                        {entry.contract}
                    </Typography>
                    <Typography
                        sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}
                    >
                        🧪 Test Mode — Fake Data
                    </Typography>
                </Box>

                <Stack direction="row" spacing={2}>
                    <TextField
                        label="Price"
                        type="number"
                        fullWidth
                        size="small"
                        value={values.price}
                        onChange={handleChange('price')}
                        inputProps={{ step: 'any' }}
                    />
                    <TextField
                        label="Quantity"
                        type="number"
                        fullWidth
                        size="small"
                        value={values.quantity}
                        onChange={handleChange('quantity')}
                        inputProps={{ step: 'any', min: 0 }}
                        disabled={values.allOrNone}
                        helperText={
                            values.allOrNone ? 'Quantity locked (AoN)' : ''
                        }
                    />
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={values.allOrNone}
                                onChange={handleChange('allOrNone')}
                            />
                        }
                        label="AoN"
                    />
                </Stack>

                <Stack direction="row" spacing={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={values.status}
                            label="Status"
                            onChange={handleChange('status')}
                        >
                            {STATUS_OPTIONS.map((s) => (
                                <MenuItem key={s} value={s}>
                                    {s}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                        <InputLabel>Expiry</InputLabel>
                        <Select
                            value={values.expiry}
                            label="Expiry"
                            onChange={handleChange('expiry')}
                        >
                            {EXPIRY_OPTIONS.map((e) => (
                                <MenuItem key={e} value={e}>
                                    {e}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="space-between"
                >
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={
                            loading ||
                            values.price === '' ||
                            values.quantity === ''
                        }
                        sx={{
                            minWidth: 140,
                            bgcolor: '#ff6d00',
                            '&:hover': { bgcolor: '#d45500' }
                        }}
                    >
                        {loading ? (
                            <CircularProgress
                                size={20}
                                sx={{ color: 'white' }}
                            />
                        ) : (
                            'Ask'
                        )}
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
}

// ── Deal Bid Form ───────────────────────────────────────────────────────────
function DealBidForm({ entry, onSubmit, loading }) {
    const [values, setValues] = useState({
        price: entry.price || '',
        quantity: entry.quantity || '',
        allOrNone: false,
        confirmDetails: false
    });

    const handleChange = (field) => (e) => {
        const next =
            field === 'confirmDetails' || field === 'allOrNone'
                ? e.target.checked
                : e.target.value;
        setValues((cur) => ({ ...cur, [field]: next }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!values.confirmDetails) return;
        onSubmit(buildDealPayload(entry, values));
    };

    return (
        <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
                <Box
                    sx={{
                        px: 2,
                        py: 2,
                        borderRadius: 2,
                        bgcolor: '#e5edf4',
                        textAlign: 'center'
                    }}
                >
                    <Typography
                        sx={{ fontSize: 28, fontWeight: 700, color: '#0f5b94' }}
                    >
                        {entry.productName}
                    </Typography>
                    <Typography
                        sx={{ fontSize: 20, fontWeight: 600, color: '#0f5b94' }}
                    >
                        {entry.contract}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: '#0f5b94' }}>
                        Deal Bid
                    </Typography>
                    <Typography
                        sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}
                    >
                        🧪 Test Mode — Fake Data
                    </Typography>
                </Box>

                <Stack direction="row" spacing={2}>
                    <TextField
                        label="Qty"
                        type="number"
                        size="small"
                        fullWidth
                        value={values.quantity}
                        onChange={handleChange('quantity')}
                        inputProps={{ min: 0, step: 'any' }}
                        disabled={values.allOrNone}
                    />
                    <TextField
                        label="Price"
                        type="number"
                        size="small"
                        fullWidth
                        value={values.price}
                        onChange={handleChange('price')}
                        inputProps={{ step: 'any' }}
                        disabled
                    />
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                        sx={{
                            color: values.allOrNone ? '#c41a1a' : '#228400',
                            fontWeight: 600
                        }}
                    >
                        {values.allOrNone
                            ? 'All or none: ON'
                            : 'All or none: OFF'}
                    </Typography>
                </Stack>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={values.confirmDetails}
                            onChange={handleChange('confirmDetails')}
                        />
                    }
                    label="I confirm these details are correct"
                />

                <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="space-between"
                >
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={
                            loading ||
                            !values.confirmDetails ||
                            values.price === '' ||
                            values.quantity === ''
                        }
                        sx={{
                            minWidth: 140,
                            bgcolor: '#c41a1a',
                            '&:hover': { bgcolor: '#a01515' }
                        }}
                    >
                        {loading ? (
                            <CircularProgress
                                size={20}
                                sx={{ color: 'white' }}
                            />
                        ) : (
                            'Sell'
                        )}
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
}

// ── Deal Ask Form ───────────────────────────────────────────────────────────
function DealAskForm({ entry, onSubmit, loading }) {
    const [values, setValues] = useState({
        price: entry.price || '',
        quantity: entry.quantity || '',
        allOrNone: false,
        confirmDetails: false
    });

    const handleChange = (field) => (e) => {
        const next =
            field === 'confirmDetails' || field === 'allOrNone'
                ? e.target.checked
                : e.target.value;
        setValues((cur) => ({ ...cur, [field]: next }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!values.confirmDetails) return;
        onSubmit(buildDealPayload(entry, values));
    };

    return (
        <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
                <Box
                    sx={{
                        px: 2,
                        py: 2,
                        borderRadius: 2,
                        bgcolor: '#e5edf4',
                        textAlign: 'center'
                    }}
                >
                    <Typography
                        sx={{ fontSize: 28, fontWeight: 700, color: '#0f5b94' }}
                    >
                        {entry.productName}
                    </Typography>
                    <Typography
                        sx={{ fontSize: 20, fontWeight: 600, color: '#0f5b94' }}
                    >
                        {entry.contract}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: '#0f5b94' }}>
                        Deal Ask
                    </Typography>
                    <Typography
                        sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}
                    >
                        🧪 Test Mode — Fake Data
                    </Typography>
                </Box>

                <Stack direction="row" spacing={2}>
                    <TextField
                        label="Qty"
                        type="number"
                        size="small"
                        fullWidth
                        value={values.quantity}
                        onChange={handleChange('quantity')}
                        inputProps={{ min: 0, step: 'any' }}
                        disabled={values.allOrNone}
                    />
                    <TextField
                        label="Price"
                        type="number"
                        size="small"
                        fullWidth
                        value={values.price}
                        onChange={handleChange('price')}
                        inputProps={{ step: 'any' }}
                        disabled
                    />
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                        sx={{
                            color: values.allOrNone ? '#c41a1a' : '#228400',
                            fontWeight: 600
                        }}
                    >
                        {values.allOrNone
                            ? 'All or none: ON'
                            : 'All or none: OFF'}
                    </Typography>
                </Stack>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={values.confirmDetails}
                            onChange={handleChange('confirmDetails')}
                        />
                    }
                    label="I confirm these details are correct"
                />

                <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="space-between"
                >
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={
                            loading ||
                            !values.confirmDetails ||
                            values.price === '' ||
                            values.quantity === ''
                        }
                        sx={{
                            minWidth: 140,
                            bgcolor: '#2f9a00',
                            '&:hover': { bgcolor: '#247900' }
                        }}
                    >
                        {loading ? (
                            <CircularProgress
                                size={20}
                                sx={{ color: 'white' }}
                            />
                        ) : (
                            'Buy'
                        )}
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
}

// ── Main Modal Component ────────────────────────────────────────────────────
export default function TestBidAskModal({ open, side = 'bid', onClose }) {
    const dispatch = useDispatch();
    const isTradeIndividualOrderLoading = useSelector(
        (state) => state.eex.isTradeIndividualOrderLoading
    );
    const isInsertOrderLoading = useSelector(
        (state) => state.eex.isInsertOrderLoading
    );

    const [activeTab, setActiveTab] = useState(0);
    const [entry] = useState(() => generateFakeEntry(side));

    const loading = isTradeIndividualOrderLoading || isInsertOrderLoading;

    const modalTitle = `${entry.productName} ${entry.contract}`;

    const handleSubmit = async (payload) => {
        try {
            if (activeTab === 0) {
                // Deal tab — call trade_individual_order
                const result = await dispatch(
                    apiTradeIndividualOrder(payload)
                ).unwrap();

                // Inspect inner EEX engine reply items for errors
                const innerItems = result?.data?.execute_reply?.items || [];
                const innerErrors = innerItems.filter(
                    (item) => item?.reply?.code && item.reply.code !== 0
                );

                if (innerErrors.length > 0) {
                    const engineMsg = innerErrors
                        .map(
                            (e) =>
                                e.reply.reason ||
                                e.reply.message ||
                                `Error code ${e.reply.code}`
                        )
                        .join('; ');
                    showToast('error', engineMsg);
                    return;
                } else {
                    showToast('success', 'Trade executed successfully');
                }
            } else {
                // Add tab — call insert_order
                const result = await dispatch(apiInsertOrder(payload)).unwrap();

                // Inspect inner EEX engine reply items for errors
                const innerItems = result?.data?.execute_reply?.items || [];
                const innerErrors = innerItems.filter(
                    (item) => item?.reply?.code && item.reply.code !== 0
                );

                if (innerErrors.length > 0) {
                    const engineMsg = innerErrors
                        .map(
                            (e) =>
                                e.reply.reason ||
                                e.reply.message ||
                                `Error code ${e.reply.code}`
                        )
                        .join('; ');
                    showToast('error', engineMsg);
                    return;
                } else {
                    const actionLabel = payload.side === 'BID' ? 'Bid' : 'Ask';
                    showToast(
                        'success',
                        `${actionLabel} order inserted successfully`
                    );
                }
            }
            onClose?.();
        } catch (error) {
            console.error(error);
            const errorMessage =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                'Failed to submit the EEX trade';
            showToast('error', errorMessage);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    px: 2,
                    pt: 2,
                    pb: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Box>
                    <Typography
                        sx={{ fontWeight: 700, fontSize: 18, color: '#245372' }}
                    >
                        {modalTitle}
                    </Typography>
                    <Typography
                        variant="caption"
                        color="warning.main"
                        sx={{ fontWeight: 600 }}
                    >
                        🧪 TEST MODE — Fake data will be posted
                    </Typography>
                </Box>
                <IconButton onClick={onClose} disabled={loading} size="small">
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Tabs */}
            <Box sx={{ px: 2, pt: 1 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, t) => setActiveTab(t)}
                    sx={{
                        minHeight: 42,
                        '& .MuiTabs-indicator': { backgroundColor: '#0b9cfd' }
                    }}
                >
                    <Tab
                        label={side === 'bid' ? 'Deal Bid' : 'Deal Ask'}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            minHeight: 42
                        }}
                    />
                    <Tab
                        label={side === 'bid' ? 'Add Bid' : 'Add Ask'}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            minHeight: 42
                        }}
                    />
                </Tabs>
            </Box>

            {/* Content */}
            <DialogContent sx={{ px: 2, pb: 2, pt: 2 }}>
                {activeTab === 0 ? (
                    side === 'bid' ? (
                        <DealBidForm
                            entry={entry}
                            onSubmit={handleSubmit}
                            loading={loading}
                        />
                    ) : (
                        <DealAskForm
                            entry={entry}
                            onSubmit={handleSubmit}
                            loading={loading}
                        />
                    )
                ) : side === 'bid' ? (
                    <AddBidForm
                        entry={entry}
                        onSubmit={handleSubmit}
                        loading={loading}
                    />
                ) : (
                    <AddAskForm
                        entry={entry}
                        onSubmit={handleSubmit}
                        loading={loading}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
}
